import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { FiPlus } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { ChainBadge } from '@/components/common/ChainBadge'
import type { ActionButton, ColumnConfig } from '@/components/common/DataTable'
import { DataTable } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { CacheRefreshIconButton, RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useToast } from '@/hooks/useToast'
import {
  useCreateOrderFeePolicy,
  useOrderFeePolicies,
  useOrderFeePolicyDetail,
  useRefreshOrderFeePolicyCache,
  useUpdateOrderFeePolicy,
} from '@/hooks/useSystemConfigData'
import type {
  OrderFeePolicy,
  OrderFeePolicyChainRate,
} from '@/services/admin/systemService'

type PolicyRow = OrderFeePolicy & {
  planLabel: string
  tierLabel: string
}

type RateDraft = {
  chainCode: string
  isTestnet: boolean
  feeRateBps: string
  minFeeUsd: string
  maxFeeUsd: string
}
type DetailRateDraft = RateDraft & {
  id: string
}
type RateDraftEditableKey = 'feeRateBps' | 'minFeeUsd' | 'maxFeeUsd'

type DetailPolicyDraft = {
  name: string
  status: '' | 'active' | 'draft' | 'archived'
  version: string
  effectiveFrom: string
  effectiveTo: string
}


const formatDateTime = (value?: string | null): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

const formatStatus = (value: string): string => {
  if (!value) return '-'
  return value.toUpperCase()
}

const parseNumeric = (
  value: string,
  label: string,
  integerOnly = false
): number => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    throw new Error(`${label} must be a number`)
  }
  if (integerOnly && !Number.isInteger(parsed)) {
    throw new Error(`${label} must be an integer`)
  }
  return parsed
}

export function FeePolicyTab(props: { cardBg: string }) {
  const { t } = useTranslation()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null)
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null)
  const [versionInput, setVersionInput] = useState('')
  const [nameInput, setNameInput] = useState('')
  const [rateDrafts, setRateDrafts] = useState<RateDraft[]>([])
  const [formError, setFormError] = useState<string | null>(null)
  const [isDetailEditing, setIsDetailEditing] = useState(false)
  const [detailPolicyDraft, setDetailPolicyDraft] = useState<DetailPolicyDraft>({
    name: '',
    status: '',
    version: '',
    effectiveFrom: '',
    effectiveTo: '',
  })
  const [detailRateDrafts, setDetailRateDrafts] = useState<DetailRateDraft[]>([])
  const [detailActivatePolicyId, setDetailActivatePolicyId] = useState('')
  const [detailFormError, setDetailFormError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    search: '',
    status: 'all' as 'all' | 'active' | 'draft' | 'archived',
    limit: 100,
  })

  const {
    data: policies,
    isFetching: isPoliciesFetching,
    refetch: refetchPolicies,
  } = useOrderFeePolicies()
  const {
    data: selectedDetail,
    isFetching: isDetailFetching,
    refetch: refetchDetail,
  } = useOrderFeePolicyDetail(selectedPolicyId ?? undefined)
  const {
    data: editingDetail,
    isFetching: isEditingFetching,
  } = useOrderFeePolicyDetail(editingPolicyId ?? undefined)

  const createPolicyMutation = useCreateOrderFeePolicy()
  const updatePolicyMutation = useUpdateOrderFeePolicy()
  const refreshPolicyCacheMutation = useRefreshOrderFeePolicyCache()

  const handleModalClose = () => {
    setEditingPolicyId(null)
    setFormError(null)
    onClose()
  }

  const handleRefresh = () => {
    refetchPolicies()
    if (selectedPolicyId) {
      refetchDetail()
    }
  }

  const handleRefreshCache = async () => {
    try {
      await refreshPolicyCacheMutation.mutateAsync()
      toast.success(t('systemConfig.feePolicy.messages.cacheRefreshed'))
      handleRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('common.error')
      toast.error(message)
    }
  }

  const rows: PolicyRow[] = useMemo(() => {
    return (policies ?? []).map(policy => ({
      ...policy,
      planLabel: policy.planName || policy.planId || '-',
      tierLabel: policy.tierLevel !== null && policy.tierLevel !== undefined
        ? `Tier ${policy.tierLevel}`
        : '-',
    }))
  }, [policies])
  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      { value: 'active', label: formatStatus('active') },
      { value: 'draft', label: formatStatus('draft') },
      { value: 'archived', label: formatStatus('archived') },
    ],
    [t]
  )
  const filteredRows = useMemo(() => {
    const needle = filters.search.trim().toLowerCase()
    return rows
      .filter(row => filters.status === 'all' || row.status === filters.status)
      .filter(row => {
        if (!needle) return true
        return (
          row.name.toLowerCase().includes(needle)
          || row.version.toLowerCase().includes(needle)
          || row.status.toLowerCase().includes(needle)
          || row.planLabel.toLowerCase().includes(needle)
          || row.tierLabel.toLowerCase().includes(needle)
        )
      })
      .slice(0, filters.limit)
  }, [filters, rows])

  const replacementPolicies = useMemo(() => {
    if (!selectedDetail) {
      return []
    }
    return (policies ?? [])
      .filter(policy => policy.id !== selectedDetail.policy.id)
      .filter(policy => policy.status !== 'active')
  }, [policies, selectedDetail])

  const requiresReplacement = Boolean(
    selectedDetail &&
      selectedDetail.policy.status === 'active' &&
      detailPolicyDraft.status &&
      detailPolicyDraft.status !== 'active'
  )

  useEffect(() => {
    if (detailPolicyDraft.status === 'active') {
      setDetailActivatePolicyId('')
    }
  }, [detailPolicyDraft.status])

  useEffect(() => {
    if (selectedPolicyId && filteredRows.some(row => row.id === selectedPolicyId)) {
      return
    }
    if (filteredRows.length > 0) {
      setSelectedPolicyId(filteredRows[0].id)
      return
    }
    setSelectedPolicyId(null)
  }, [filteredRows, selectedPolicyId])

  useEffect(() => {
    setIsDetailEditing(false)
    setDetailFormError(null)
  }, [selectedPolicyId])

  useEffect(() => {
    if (!editingDetail) {
      return
    }
    setVersionInput('')
    setNameInput('')
    setFormError(null)
    const drafts = (editingDetail.chainRates ?? []).map(rate => ({
      chainCode: rate.chainCode,
      isTestnet: rate.isTestnet,
      feeRateBps: String(rate.feeRateBps),
      minFeeUsd: String(rate.minFeeUsd),
      maxFeeUsd: String(rate.maxFeeUsd),
    }))
    setRateDrafts(drafts)
  }, [editingDetail])

  useEffect(() => {
    if (!selectedDetail || isDetailEditing) {
      return
    }
    setDetailPolicyDraft({
      name: selectedDetail.policy.name,
      status: selectedDetail.policy.status,
      version: selectedDetail.policy.version,
      effectiveFrom: selectedDetail.policy.effectiveFrom ?? '',
      effectiveTo: selectedDetail.policy.effectiveTo ?? '',
    })
    const drafts = (selectedDetail.chainRates ?? []).map(rate => ({
      id: rate.id,
      chainCode: rate.chainCode,
      isTestnet: rate.isTestnet,
      feeRateBps: String(rate.feeRateBps),
      minFeeUsd: String(rate.minFeeUsd),
      maxFeeUsd: String(rate.maxFeeUsd),
    }))
    setDetailRateDrafts(drafts)
    setDetailActivatePolicyId('')
    setDetailFormError(null)
  }, [selectedDetail, isDetailEditing])

  const columns: ColumnConfig<PolicyRow>[] = useMemo(
    () => [
      {
        key: 'name',
        title: t('systemConfig.feePolicy.columns.name'),
        render: (value: string, row) => (
          <VStack align="start" spacing={0}>
            <Text fontWeight="medium">{value}</Text>
            <Text fontSize="xs" color="gray.500">
              {row.planLabel} {row.tierLabel !== '-' ? `· ${row.tierLabel}` : ''}
            </Text>
          </VStack>
        ),
      },
      {
        key: 'version',
        title: t('systemConfig.feePolicy.columns.version'),
      },
      {
        key: 'status',
        title: t('systemConfig.feePolicy.columns.status'),
        render: (value: string) => (
          <Badge colorScheme={value === 'active' ? 'green' : value === 'draft' ? 'yellow' : 'gray'}>
            {formatStatus(value)}
          </Badge>
        ),
      },
      {
        key: 'updatedAt',
        title: t('systemConfig.feePolicy.columns.updatedAt'),
        render: (value: string) => formatDateTime(value),
      },
    ],
    [t]
  )

  const actions = (row: PolicyRow): ActionButton[] => [
    {
      label: t('systemConfig.feePolicy.actions.newVersion'),
      onClick: () => {
        setEditingPolicyId(row.id)
        onOpen()
      },
      colorScheme: 'blue',
      size: 'sm',
      icon: <FiPlus />,
      disabled: row.status !== 'active',
      tooltip:
        row.status !== 'active'
          ? t('systemConfig.feePolicy.actions.activeOnly')
          : undefined,
    },
  ]

  const handleRateChange = (index: number, key: RateDraftEditableKey, value: string) => {
    setRateDrafts(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [key]: value }
      return next
    })
  }

  const handleDetailRateChange = (
    index: number,
    key: RateDraftEditableKey,
    value: string
  ) => {
    setDetailRateDrafts(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [key]: value }
      return next
    })
  }

  const startDetailEdit = () => {
    if (!selectedDetail) {
      return
    }
    setDetailPolicyDraft({
      name: selectedDetail.policy.name,
      status: selectedDetail.policy.status,
      version: selectedDetail.policy.version,
      effectiveFrom: selectedDetail.policy.effectiveFrom ?? '',
      effectiveTo: selectedDetail.policy.effectiveTo ?? '',
    })
    setDetailRateDrafts(
      (selectedDetail.chainRates ?? []).map(rate => ({
        id: rate.id,
        chainCode: rate.chainCode,
        isTestnet: rate.isTestnet,
        feeRateBps: String(rate.feeRateBps),
        minFeeUsd: String(rate.minFeeUsd),
        maxFeeUsd: String(rate.maxFeeUsd),
      }))
    )
    setDetailFormError(null)
    setDetailActivatePolicyId('')
    setIsDetailEditing(true)
  }

  const cancelDetailEdit = () => {
    setIsDetailEditing(false)
    setDetailFormError(null)
    setDetailActivatePolicyId('')
    if (selectedDetail) {
      setDetailPolicyDraft({
        name: selectedDetail.policy.name,
        status: selectedDetail.policy.status,
        version: selectedDetail.policy.version,
        effectiveFrom: selectedDetail.policy.effectiveFrom ?? '',
        effectiveTo: selectedDetail.policy.effectiveTo ?? '',
      })
      setDetailRateDrafts(
        (selectedDetail.chainRates ?? []).map(rate => ({
          id: rate.id,
          chainCode: rate.chainCode,
          isTestnet: rate.isTestnet,
          feeRateBps: String(rate.feeRateBps),
          minFeeUsd: String(rate.minFeeUsd),
          maxFeeUsd: String(rate.maxFeeUsd),
        }))
      )
    }
  }

  const handleDetailSave = async () => {
    if (!selectedDetail) {
      return
    }
    if (!detailPolicyDraft.name.trim()) {
      setDetailFormError(t('systemConfig.feePolicy.errors.nameMissing'))
      return
    }
    if (!/^v[0-9]+$/u.test(detailPolicyDraft.version.trim())) {
      setDetailFormError(t('systemConfig.feePolicy.errors.versionFormat'))
      return
    }
    if (detailRateDrafts.length === 0) {
      setDetailFormError(t('systemConfig.feePolicy.errors.ratesMissing'))
      return
    }

    const needsActivatePolicy =
      selectedDetail.policy.status === 'active' && detailPolicyDraft.status !== 'active'
    if (needsActivatePolicy) {
      if (replacementPolicies.length === 0) {
        setDetailFormError(t('systemConfig.feePolicy.errors.activatePolicyUnavailable'))
        return
      }
      if (!detailActivatePolicyId) {
        setDetailFormError(t('systemConfig.feePolicy.errors.activatePolicyRequired'))
        return
      }
    }

    try {
      const chainRates = detailRateDrafts.map(rate => ({
        chainCode: rate.chainCode,
        feeRateBps: parseNumeric(rate.feeRateBps, 'feeRateBps', true),
        minFeeUsd: parseNumeric(rate.minFeeUsd, 'minFeeUsd'),
        maxFeeUsd: parseNumeric(rate.maxFeeUsd, 'maxFeeUsd'),
      }))

      await updatePolicyMutation.mutateAsync({
        policyId: selectedDetail.policy.id,
        updates: {
          name: detailPolicyDraft.name.trim(),
          status: detailPolicyDraft.status || undefined,
          activatePolicyId: needsActivatePolicy ? detailActivatePolicyId : undefined,
          version: detailPolicyDraft.version.trim(),
          effectiveFrom: detailPolicyDraft.effectiveFrom.trim() || null,
          effectiveTo: detailPolicyDraft.effectiveTo.trim() || null,
          chainRates,
        },
      })

      try {
        await refreshPolicyCacheMutation.mutateAsync()
      } catch (error) {
        console.warn('[FeePolicyTab] Failed to refresh fee policy cache', error)
      }

      toast.success(t('systemConfig.feePolicy.messages.updated'))
      setIsDetailEditing(false)
      setDetailFormError(null)
      refetchPolicies()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('common.error')
      setDetailFormError(message)
    }
  }

  const handleCreate = async (activate: boolean) => {
    if (!editingPolicyId) {
      setFormError(t('systemConfig.feePolicy.errors.policyMissing'))
      return
    }
    if (!/^v[0-9]+$/u.test(versionInput.trim())) {
      setFormError(t('systemConfig.feePolicy.errors.versionFormat'))
      return
    }
    if (rateDrafts.length === 0) {
      setFormError(t('systemConfig.feePolicy.errors.ratesMissing'))
      return
    }
    try {
      const chainRates = rateDrafts.map(rate => ({
        chainCode: rate.chainCode,
        feeRateBps: parseNumeric(rate.feeRateBps, 'feeRateBps'),
        minFeeUsd: parseNumeric(rate.minFeeUsd, 'minFeeUsd'),
        maxFeeUsd: parseNumeric(rate.maxFeeUsd, 'maxFeeUsd'),
      }))

      const created = await createPolicyMutation.mutateAsync({
        parentPolicyId: editingPolicyId,
        version: versionInput.trim(),
        name: nameInput.trim().length > 0 ? nameInput.trim() : undefined,
        activate,
        chainRates,
      })

      try {
        await refreshPolicyCacheMutation.mutateAsync()
      } catch (error) {
        console.warn('[FeePolicyTab] Failed to refresh fee policy cache', error)
      }

      toast.success(t('systemConfig.feePolicy.messages.created'))
      const createdPolicyId = created?.policy?.id ?? null
      handleModalClose()
      if (createdPolicyId) {
        setSelectedPolicyId(createdPolicyId)
      }
      refetchPolicies()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('common.error')
      setFormError(message)
    }
  }

  const detailRates: OrderFeePolicyChainRate[] = selectedDetail?.chainRates ?? []

  return (
    <VStack spacing={4} align="stretch">
      <Card bg={props.cardBg}>
        <CardHeader>
          <Flex
            direction={{ base: 'column', xl: 'row' }}
            align={{ base: 'stretch', xl: 'end' }}
            justify="space-between"
            gap={3}
          >
            <HStack spacing={3} align="end" flexWrap={{ base: 'wrap', xl: 'nowrap' }} flex="1" minW={0}>
              <Heading size="sm" minW="fit-content">
                {t('systemConfig.feePolicy.title')}
              </Heading>
              <FormControl minW={{ base: '100%', md: '220px' }} maxW={{ xl: '220px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.search', { defaultValue: 'Search' })}
                </FormLabel>
                <Input
                  size="xs"
                  value={filters.search}
                  onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
                  placeholder={t('common.search', { defaultValue: 'Search' })}
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '120px' }} maxW={{ xl: '120px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('systemConfig.feePolicy.columns.status')}
                </FormLabel>
                <DropdownSelect
                  value={filters.status}
                  options={statusOptions}
                  onChange={value =>
                    setFilters(prev => ({ ...prev, status: value as typeof filters.status }))
                  }
                  size="xs"
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '100px' }} maxW={{ xl: '100px' }}>
                <FormLabel fontSize="xs" mb={1}>{t('common.limit')}</FormLabel>
                <DropdownSelect
                  value={String(filters.limit)}
                  options={[50, 100, 200, 500].map(limit => ({
                    value: String(limit),
                    label: String(limit),
                  }))}
                  onChange={value => setFilters(prev => ({ ...prev, limit: Number(value) }))}
                  size="xs"
                />
              </FormControl>
            </HStack>
            <HStack spacing={2} justify={{ base: 'flex-start', xl: 'flex-end' }} flexShrink={0}>
              <RefreshIconButton
                label={t('common.refresh')}
                onClick={handleRefresh}
                isLoading={isPoliciesFetching}
              />
              <CacheRefreshIconButton
                label={t('systemConfig.feePolicy.actions.refreshCache')}
                onClick={handleRefreshCache}
                isLoading={refreshPolicyCacheMutation.isPending}
              />
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <DataTable
            data={filteredRows}
            loading={isPoliciesFetching}
            columns={columns}
            actions={actions}
            onRowClick={row => setSelectedPolicyId(row.id)}
            showRefresh
            refreshing={isPoliciesFetching}
            onRefresh={() => refetchPolicies()}
            emptyMessage={t('systemConfig.feePolicy.empty')}
          />
        </CardBody>
      </Card>

      <Card bg={props.cardBg}>
        <CardHeader>
          <HStack justify="space-between" align="center">
            <Heading size="md">{t('systemConfig.feePolicy.detailTitle')}</Heading>
            {selectedDetail && (
              isDetailEditing ? (
                <HStack spacing={2}>
                  <Button variant="ghost" size="sm" onClick={cancelDetailEdit}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={handleDetailSave}
                    isLoading={updatePolicyMutation.isPending}
                  >
                    {t('systemConfig.feePolicy.actions.save')}
                  </Button>
                </HStack>
              ) : (
                <Button size="sm" onClick={startDetailEdit}>
                  {t('systemConfig.feePolicy.actions.edit')}
                </Button>
              )
            )}
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          {selectedDetail ? (
            <VStack align="stretch" spacing={4}>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                <GridItem>
                  <Text fontSize="sm" color="gray.500">
                    {t('systemConfig.feePolicy.labels.policy')}
                  </Text>
                  {isDetailEditing ? (
                    <Input
                      value={detailPolicyDraft.name}
                      onChange={event =>
                        setDetailPolicyDraft(prev => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                    />
                  ) : (
                    <Text fontWeight="medium">{selectedDetail.policy.name}</Text>
                  )}
                </GridItem>
                <GridItem>
                  <Text fontSize="sm" color="gray.500">
                    {t('systemConfig.feePolicy.labels.version')}
                  </Text>
                  {isDetailEditing ? (
                    <Input
                      value={detailPolicyDraft.version}
                      onChange={event =>
                        setDetailPolicyDraft(prev => ({
                          ...prev,
                          version: event.target.value,
                        }))
                      }
                    />
                  ) : (
                    <Text fontWeight="medium">{selectedDetail.policy.version}</Text>
                  )}
                </GridItem>
                <GridItem>
                  <Text fontSize="sm" color="gray.500">
                    {t('systemConfig.feePolicy.labels.status')}
                  </Text>
                  {isDetailEditing ? (
                    <DropdownSelect
                      value={detailPolicyDraft.status}
                      options={[
                        { value: 'active', label: formatStatus('active') },
                        { value: 'draft', label: formatStatus('draft') },
                        { value: 'archived', label: formatStatus('archived') },
                      ]}
                      onChange={value =>
                        setDetailPolicyDraft(prev => ({
                          ...prev,
                          status: value as DetailPolicyDraft['status'],
                        }))
                      }
                      ariaLabel={t('systemConfig.feePolicy.labels.status')}
                    />
                  ) : (
                    <Badge colorScheme={selectedDetail.policy.status === 'active' ? 'green' : 'gray'}>
                      {formatStatus(selectedDetail.policy.status)}
                    </Badge>
                  )}
                </GridItem>
                {isDetailEditing && requiresReplacement && (
                  <GridItem colSpan={{ base: 1, md: 3 }}>
                    <Text fontSize="sm" color="gray.500">
                      {t('systemConfig.feePolicy.labels.activatePolicy')}
                    </Text>
                    <DropdownSelect
                      value={detailActivatePolicyId}
                      options={replacementPolicies.map(policy => ({
                        value: policy.id,
                        label:
                          `${policy.name} · ${policy.version} · ` +
                          formatStatus(policy.status),
                      }))}
                      onChange={setDetailActivatePolicyId}
                      placeholder={t('systemConfig.feePolicy.labels.activatePolicyPlaceholder')}
                      emptyOptionLabel={t(
                        'systemConfig.feePolicy.labels.activatePolicyPlaceholder'
                      )}
                      ariaLabel={t('systemConfig.feePolicy.labels.activatePolicy')}
                    />
                    {replacementPolicies.length === 0 && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        {t('systemConfig.feePolicy.errors.activatePolicyUnavailable')}
                      </Text>
                    )}
                  </GridItem>
                )}
                <GridItem>
                  <Text fontSize="sm" color="gray.500">
                    {t('systemConfig.feePolicy.labels.effectiveFrom')}
                  </Text>
                  {isDetailEditing ? (
                    <Input
                      value={detailPolicyDraft.effectiveFrom}
                      onChange={event =>
                        setDetailPolicyDraft(prev => ({
                          ...prev,
                          effectiveFrom: event.target.value,
                        }))
                      }
                      placeholder="2025-01-01T00:00:00.000Z"
                    />
                  ) : (
                    <Text fontWeight="medium">
                      {selectedDetail.policy.effectiveFrom || '-'}
                    </Text>
                  )}
                </GridItem>
                <GridItem>
                  <Text fontSize="sm" color="gray.500">
                    {t('systemConfig.feePolicy.labels.effectiveTo')}
                  </Text>
                  {isDetailEditing ? (
                    <Input
                      value={detailPolicyDraft.effectiveTo}
                      onChange={event =>
                        setDetailPolicyDraft(prev => ({
                          ...prev,
                          effectiveTo: event.target.value,
                        }))
                      }
                      placeholder="2025-01-31T00:00:00.000Z"
                    />
                  ) : (
                    <Text fontWeight="medium">
                      {selectedDetail.policy.effectiveTo || '-'}
                    </Text>
                  )}
                </GridItem>
              </Grid>

              {detailFormError && (
                <Text color="red.500" fontSize="sm">
                  {detailFormError}
                </Text>
              )}

              <TableContainer width="100%" overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>{t('systemConfig.feePolicy.columns.chain')}</Th>
                      <Th>{t('systemConfig.feePolicy.columns.feeRateBps')}</Th>
                      <Th>{t('systemConfig.feePolicy.columns.minFeeUsd')}</Th>
                      <Th>{t('systemConfig.feePolicy.columns.maxFeeUsd')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(isDetailEditing ? detailRateDrafts : detailRates).map((rate, index) => (
                      <Tr key={rate.id ?? rate.chainCode}>
                        <Td>
                          <ChainBadge
                            chainId={rate.chainCode}
                            chainCode={rate.chainCode}
                            isTestnet={rate.isTestnet}
                          />
                        </Td>
                        <Td>
                          {isDetailEditing ? (
                            <Input
                              value={rate.feeRateBps}
                              onChange={event =>
                                handleDetailRateChange(index, 'feeRateBps', event.target.value)
                              }
                            />
                          ) : (
                            rate.feeRateBps
                          )}
                        </Td>
                        <Td>
                          {isDetailEditing ? (
                            <Input
                              value={rate.minFeeUsd}
                              onChange={event =>
                                handleDetailRateChange(index, 'minFeeUsd', event.target.value)
                              }
                            />
                          ) : (
                            rate.minFeeUsd
                          )}
                        </Td>
                        <Td>
                          {isDetailEditing ? (
                            <Input
                              value={rate.maxFeeUsd}
                              onChange={event =>
                                handleDetailRateChange(index, 'maxFeeUsd', event.target.value)
                              }
                            />
                          ) : (
                            rate.maxFeeUsd
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          ) : (
            <Text color="gray.500">
              {isDetailFetching
                ? t('common.loading')
                : t('systemConfig.feePolicy.empty')}
            </Text>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={handleModalClose} size="6xl">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>{t('systemConfig.feePolicy.modal.title')}</ModalHeader>
          <ModalCloseButton onClick={handleModalClose} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>{t('systemConfig.feePolicy.modal.version')}</FormLabel>
                <Input
                  value={versionInput}
                  onChange={event => setVersionInput(event.target.value)}
                  placeholder="v2"
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('systemConfig.feePolicy.modal.name')}</FormLabel>
                <Input
                  value={nameInput}
                  onChange={event => setNameInput(event.target.value)}
                  placeholder={t('systemConfig.feePolicy.modal.namePlaceholder')}
                />
              </FormControl>
              {formError && (
                <Text color="red.500" fontSize="sm">
                  {formError}
                </Text>
              )}
              {isEditingFetching ? (
                <Text>{t('common.loading')}</Text>
              ) : (
                <TableContainer width="100%" overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>{t('systemConfig.feePolicy.columns.chain')}</Th>
                        <Th>{t('systemConfig.feePolicy.columns.feeRateBps')}</Th>
                        <Th>{t('systemConfig.feePolicy.columns.minFeeUsd')}</Th>
                        <Th>{t('systemConfig.feePolicy.columns.maxFeeUsd')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {rateDrafts.map((rate, index) => (
                        <Tr key={rate.chainCode}>
                          <Td>
                            <ChainBadge
                              chainId={rate.chainCode}
                              chainCode={rate.chainCode}
                              isTestnet={rate.isTestnet}
                            />
                          </Td>
                          <Td>
                            <Input
                              value={rate.feeRateBps}
                              onChange={event => handleRateChange(index, 'feeRateBps', event.target.value)}
                            />
                          </Td>
                          <Td>
                            <Input
                              value={rate.minFeeUsd}
                              onChange={event => handleRateChange(index, 'minFeeUsd', event.target.value)}
                            />
                          </Td>
                          <Td>
                            <Input
                              value={rate.maxFeeUsd}
                              onChange={event => handleRateChange(index, 'maxFeeUsd', event.target.value)}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => handleCreate(false)}
              isLoading={createPolicyMutation.isPending}
            >
              {t('systemConfig.feePolicy.actions.createDraft')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => handleCreate(true)}
              isLoading={createPolicyMutation.isPending}
            >
              {t('systemConfig.feePolicy.actions.create')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
